import type { EnhancedMessageTrackingData } from '../services/outreach/types';

export type EngagementTimeRange = '7d' | '30d' | '90d' | '180d';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const TIME_RANGE_TO_DAYS: Record<EngagementTimeRange, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
};

export const getDaysFromRange = (range: EngagementTimeRange): number =>
  TIME_RANGE_TO_DAYS[range] ?? TIME_RANGE_TO_DAYS['30d'];

export const toDateKey = (timestamp: number): string =>
  new Date(timestamp).toISOString().split('T')[0];

export const roundToTwo = (value: number): number =>
  Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;

export const normalizeRecipient = (recipient?: string, fallback?: string): string => {
  if (recipient && typeof recipient === 'string') {
    return recipient.trim().toLowerCase();
  }
  if (fallback) {
    return fallback.trim().toLowerCase();
  }
  return 'unknown';
};

export const parseTimestamp = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  const asNumber = Number(value);
  if (!Number.isNaN(asNumber)) {
    return asNumber > 1e12 ? asNumber : asNumber * 1000;
  }

  return null;
};

const fallbackTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.timestamp) ?? null;

export const getDeliveredTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.deliveredAt) ??
  (record.status === 'delivered' || record.status === 'opened' || record.status === 'clicked'
    ? fallbackTimestamp(record)
    : null);

export const getOpenedTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.openedAt) ??
  (record.status === 'opened' ? fallbackTimestamp(record) : null);

export const getClickedTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.clickedAt) ??
  (record.status === 'clicked' ? fallbackTimestamp(record) : null);

export const getBouncedTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.bouncedAt) ??
  (record.status === 'failed' ? fallbackTimestamp(record) : null);

export const getComplaintTimestamp = (record: EnhancedMessageTrackingData): number | null =>
  parseTimestamp(record.complainedAt);

export const isWithinRange = (timestamp: number | null | undefined, start: number, end: number): boolean =>
  typeof timestamp === 'number' && timestamp >= start && timestamp < end;

export const hasOpenedEvent = (record: EnhancedMessageTrackingData): boolean =>
  Boolean(getOpenedTimestamp(record));

export const hasClickedEvent = (record: EnhancedMessageTrackingData): boolean =>
  Boolean(getClickedTimestamp(record));

export const hasBounceEvent = (record: EnhancedMessageTrackingData): boolean =>
  Boolean(getBouncedTimestamp(record));

export const hasComplaintEvent = (record: EnhancedMessageTrackingData): boolean =>
  Boolean(getComplaintTimestamp(record));

export const hadSuccessfulDelivery = (record: EnhancedMessageTrackingData): boolean =>
  Boolean(getDeliveredTimestamp(record));

export const createRange = (days: number): { start: number; end: number } => {
  const end = Date.now();
  return {
    start: end - days * DAY_IN_MS,
    end,
  };
};
